import { useState, useEffect, useContext } from "react";
import {
  FaBox,
  
  FaExchangeAlt,
  FaClipboardList,
  FaChartLine,
  FaSearch,
 
  FaUserCircle,
  FaBars,
  FaTimes,
} from "react-icons/fa";
import InventoryDashboard from "./inventory/InventoryDashboard";
import InventoryItems from "./inventory/InventoryItems";
import StockMovements from "./inventory/StockMovements";
import Reports from "./inventory/Reports";
import InventoryAnalytics from "./inventory/InventoryAnalytics";
import AuthContext from "../contexts/AuthContext";
import { getRestaurantProfile } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Supplier from "./inventory/Supplier";
import OpeningStock from "./inventory/OpeningStock";
import MainStore from "./inventory/MainStore";

const inventorySidebar = [
  { name: "Dashboard", label: "Overview", icon: <FaBox size={20} /> },
  {
    name: "Transactions",
    label: "Transactions",
    icon: <FaExchangeAlt size={20} />,
    dropdown: true,
    children: [
      { name: "Supplier", label: "Add Supplier" },
      { name: "Items", label: "Add Items" },
      { name: "OpeningStock", label: "Opening Stock" },
      { name: "MainStore", label: "Main Store" },
    ],
  },
  {
    name: "Movements",
    label: "Stock Movements",
    icon: <FaExchangeAlt size={20} />,
  },
  {
    name: "Reports",
    label: "Reports",
    icon: <FaClipboardList size={20} />,
  },
  { name: "Analytics", label: "Analytics", icon: <FaChartLine size={20} /> },
];

export default function InventoryHome() {
  const [active, setActive] = useState("Dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { restaurantId } = useContext(AuthContext);
  const { logout } = useAuth();
  const [restaurantName, setRestaurantName] = useState("");
  const [showTransactionDropdown, setShowTransactionDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchName() {
      if (!restaurantId) return;
      try {
        const profile = await getRestaurantProfile(restaurantId);
        setRestaurantName(profile.name || "My Restaurant");
      } catch (err) {
        setRestaurantName("My Restaurant");
      }
    }
    fetchName();
  }, [restaurantId]);

  // Close sidebar on mobile when screen size changes
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-dropdown')) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown]);

  async function handleLogout() {
    try {
      await logout();
      localStorage.clear();
      navigate("/");
    } catch (err) {
      console.error("Logout failed:", err);
      localStorage.clear();
      navigate("/");
    }
  }

  function handleAddSupplier() {
    setActive("Supplier");
    setShowTransactionDropdown(false);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }
  function handleAddItems() {
    setActive("Items");
    setShowTransactionDropdown(false);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }
  function handleOpeningStock() {
    setActive("OpeningStock");
    setShowTransactionDropdown(false);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }
  function handleMainStore() {
    setActive("MainStore");
    setShowTransactionDropdown(false);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }

  const handleSidebarItemClick = (itemName) => {
    setActive(itemName);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col h-screen transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        {/* Restaurant name and search */}
        <div className="flex flex-col gap-2 p-3 sm:p-4 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <button
              className="lg:hidden mr-2 p-1"
              onClick={() => setSidebarOpen(false)}
            >
              <FaTimes size={18} />
            </button>
            <span className="font-bold text-sm sm:text-base lg:text-lg text-gray-800 truncate max-w-[10rem]">
              {restaurantName}
            </span>
          </div>
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Menu Search"
              className="pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 border border-gray-200 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-rose-500 bg-white text-sm"
            />
          </div>
        </div>

        {/* Sidebar menu */}
        <nav className="flex-1 py-3 sm:py-4 overflow-y-auto">
          {/* Dashboard */}
          <button
            className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 w-full text-sm sm:text-base font-medium transition rounded-lg mb-1 ${
              active === "Dashboard"
                ? "bg-gray-200 text-primary"
                : "text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => handleSidebarItemClick("Dashboard")}
          >
            <span>
              <FaBox size={16} className="sm:w-5 sm:h-5" />
            </span>
            <span>Overview</span>
          </button>

          {/* Transactions collapsible */}
          <div className="relative mb-1">
            <button
              className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 w-full text-sm sm:text-base font-medium transition rounded-lg ${
                active === "Transactions" || showTransactionDropdown
                  ? "bg-gray-200 text-primary"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
              onClick={() => setShowTransactionDropdown((v) => !v)}
              type="button"
            >
              <span>
                <FaExchangeAlt size={16} className="sm:w-5 sm:h-5" />
              </span>
              <span>Transactions</span>
              <span className="ml-auto text-xs">▼</span>
            </button>
            {showTransactionDropdown && (
              <div className="flex flex-col ml-4 sm:ml-6 lg:ml-8 mt-1">
                <button
                  className={`block w-full text-left px-3 sm:px-4 py-2 rounded hover:bg-gray-100 text-gray-700 text-xs sm:text-sm ${
                    active === "Supplier" ? "font-bold bg-gray-100" : ""
                  }`}
                  onClick={handleAddSupplier}
                >
                  Add Supplier
                </button>
                <button
                  className={`block w-full text-left px-3 sm:px-4 py-2 rounded hover:bg-gray-100 text-gray-700 text-xs sm:text-sm ${
                    active === "Items" ? "font-bold bg-gray-100" : ""
                  }`}
                  onClick={handleAddItems}
                >
                  Add Items
                </button>
                <button
                  className={`block w-full text-left px-3 sm:px-4 py-2 rounded hover:bg-gray-100 text-gray-700 text-xs sm:text-sm ${
                    active === "OpeningStock" ? "font-bold bg-gray-100" : ""
                  }`}
                  onClick={handleOpeningStock}
                >
                  Opening Stock
                </button>
                <button
                  className={`block w-full text-left px-3 sm:px-4 py-2 rounded hover:bg-gray-100 text-gray-700 text-xs sm:text-sm ${
                    active === "MainStore" ? "font-bold bg-gray-100" : ""
                  }`}
                  onClick={handleMainStore}
                >
                  Main Store
                </button>
              </div>
            )}
          </div>

          {/* The rest of the sidebar items */}
          <button
            className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 w-full text-sm sm:text-base font-medium transition rounded-lg mb-1 ${
              active === "Movements"
                ? "bg-gray-200 text-primary"
                : "text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => handleSidebarItemClick("Movements")}
          >
            <span>
              <FaExchangeAlt size={16} className="sm:w-5 sm:h-5" />
            </span>
            <span>Stock Movements</span>
          </button>
          <button
            className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 w-full text-sm sm:text-base font-medium transition rounded-lg mb-1 ${
              active === "Reports"
                ? "bg-gray-200 text-primary"
                : "text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => handleSidebarItemClick("Reports")}
          >
            <span>
              <FaClipboardList size={16} className="sm:w-5 sm:h-5" />
            </span>
            <span>Reports</span>
          </button>

          <button
            className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 w-full text-sm sm:text-base font-medium transition rounded-lg mb-1 ${
              active === "Analytics"
                ? "bg-gray-200 text-primary"
                : "text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => handleSidebarItemClick("Analytics")}
          >
            <span>
              <FaChartLine size={16} className="sm:w-5 sm:h-5" />
            </span>
            <span>Analytics</span>
          </button>
        </nav>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="flex items-center justify-between bg-white border-b border-gray-200 px-3 sm:px-4 lg:px-6 xl:px-8 py-3 shadow-sm sticky top-0 z-10">
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <FaBars size={16} />
            </button>
            <span className="font-bold text-sm sm:text-base lg:text-lg text-gray-800 truncate max-w-[10rem] sm:max-w-[12rem] lg:max-w-[16rem]">
              {restaurantName}
            </span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 lg:gap-4">
            <div className="relative user-dropdown">
              <button
                onClick={toggleUserDropdown}
                className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <FaUserCircle size={16} className="sm:w-5 sm:h-5" />
              </button>
              {showUserDropdown && (
                <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-3 sm:p-4 lg:p-6 xl:p-10 overflow-y-auto bg-white">
          <div className="max-w-6xl mx-auto">
            {active === "Dashboard" && <InventoryDashboard />}
            {active === "Items" && <InventoryItems />}
            {active === "Supplier" && <Supplier />}
            {active === "OpeningStock" && <OpeningStock />}
            {active === "MainStore" && <MainStore />}
            {active === "Movements" && <StockMovements />}
            {active === "Reports" && <Reports />}
            {active === "Analytics" && <InventoryAnalytics />}
          </div>
        </main>
      </div>
    </div>
  );
}
